/**
 * =============================================================================
 * Hook: useAffiliateProgramInvites
 * Versão: 1.0.0
 *
 * Descrição:
 *   Gerencia convites de programas privados de afiliado (invite_only) via as
 *   RPCs `list_affiliate_program_invites`, `create_affiliate_program_invite`,
 *   `revoke_affiliate_program_invite` e a Edge Function `send-affiliate-invite`.
 *
 * Invariante:
 *   Nenhuma ação deste hook concede acesso ao painel do tenant. Apenas cria
 *   vínculos em `affiliate_program_memberships` após aceite legítimo.
 * =============================================================================
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const log = createLogger('useAffiliateProgramInvites');

export interface AffiliateProgramInvite {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  custom_commission_value: number | null;
  token: string;
  expires_at: string;
  accepted_at: string | null;
  sent_count: number;
  last_sent_at: string | null;
  last_error: string | null;
  invited_by_user_id: string | null;
  invited_by_name: string | null;
  created_at: string;
  total_count: number;
}

export interface ListInvitesParams {
  programId: string;
  status?: string | null;
  search?: string | null;
  limit?: number;
  offset?: number;
}

/** Lista paginada de convites de um programa. */
export function useAffiliateProgramInvites(params: ListInvitesParams) {
  return useQuery({
    queryKey: [
      'affiliate-program-invites',
      params.programId,
      params.status ?? null,
      params.search ?? null,
      params.limit ?? 50,
      params.offset ?? 0,
    ],
    enabled: !!params.programId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'list_affiliate_program_invites',
        {
          p_program_id: params.programId,
          p_status: params.status ?? null,
          p_search: params.search ?? null,
          p_limit: params.limit ?? 50,
          p_offset: params.offset ?? 0,
        } as any,
      );
      if (error) {
        log.error('Falha ao listar convites', error);
        throw error;
      }
      return (data ?? []) as AffiliateProgramInvite[];
    },
  });
}

export interface CreateInviteInput {
  programId: string;
  email: string;
  name?: string | null;
  phone?: string | null;
  campaignId?: string | null;
  customCommission?: number | null;
}

/**
 * Cria convite (e opcionalmente dispara e-mail via Edge Function).
 * Retorna o resultado da RPC — quando `auto_linked=true`, não envia e-mail.
 */
export function useCreateAffiliateProgramInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateInviteInput) => {
      const { data, error } = await supabase.rpc(
        'create_affiliate_program_invite',
        {
          p_program_id: input.programId,
          p_email: input.email,
          p_name: input.name ?? null,
          p_phone: input.phone ?? null,
          p_campaign_id: input.campaignId ?? null,
          p_custom_commission: input.customCommission ?? null,
        } as any,
      );
      if (error) throw error;
      const row = (data as any[])?.[0];
      if (!row) throw new Error('Falha ao criar convite');

      // Se não foi auto-vinculado, envia o e-mail
      if (!row.auto_linked && row.invite_id) {
        const { error: sendErr } = await supabase.functions.invoke(
          'send-affiliate-invite',
          { body: { invite_id: row.invite_id } },
        );
        if (sendErr) {
          log.warn('Falha ao enviar e-mail do convite', sendErr);
          throw new Error(
            'Convite criado, mas falhou ao enviar o e-mail. Tente reenviar.',
          );
        }
      }
      return row as {
        invite_id: string;
        token: string;
        status: string;
        auto_linked: boolean;
        already_member: boolean;
      };
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['affiliate-program-invites', vars.programId],
      });
    },
  });
}

/** Revoga um convite pendente. */
export function useRevokeAffiliateProgramInvite(programId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { error } = await supabase.rpc(
        'revoke_affiliate_program_invite',
        { p_invite_id: inviteId } as any,
      );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['affiliate-program-invites', programId],
      });
    },
  });
}

/** Reenvia o e-mail de um convite pendente. */
export function useResendAffiliateProgramInvite(programId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { error } = await supabase.functions.invoke(
        'send-affiliate-invite',
        { body: { invite_id: inviteId } },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ['affiliate-program-invites', programId],
      });
    },
  });
}
